import "dayjs/locale/ko";
import {
  AiCommentType,
  AudioPlayerType,
  CellType,
  ContentType,
  DataMappingType,
  ElementType,
  MultipleSelectionType,
  PercentageType,
  PresetType,
  PushNotificationType,
  SelfCheckType,
  SingleSelectionType,
  TextareaType,
} from "../data/CellComponent";
import * as _ from "lodash";
import { UserType } from "recoil/userAtom";
import {
  AiCommentWithTextareaInput,
  ProgramContentType,
} from "data/BlockComponent";
import { ProgramType, TaskMetaData } from "data/programData";
import axios from "../utils/axios";
import { KeyPrefix, Namespace, TFunction } from "i18next";
import { FallbackNs } from "react-i18next";
import {
  automaticThoughtIdType,
  ConceptNoteType,
  ConceptType,
} from "api/conceptApi";
import { CommentType } from "api/aiApi";
import { TempTaskDataType } from "recoil/taskDataAtom";
import { arraySum } from "./logics_common";
import { v4 as uuidv4 } from "uuid";
import { getTaskEditorTaskTitle } from "pages/task-editor/utils/taskEditorRuntime";

// 바로 다음블록 열어야하는 경우
export function hasBlockUserField(data: ProgramContentType) {
  const elements = data.lines.flat();

  return elements.some(
    (each) =>
      ["textarea", "select", "buttongroup", "selfCheck", "feedback"].includes(
        each.type
      ) && !each.content.coach
  );
}

export function isBlockUserFieldFilled(
  data: ProgramContentType,
  totalData?: ProgramContentType[],
  user?: UserType
) {
  const elements = data.lines
    .filter((line) =>
      !totalData ? true : !isLineHidden(totalData, data, line)
    )
    .flat()
    .filter((element) => !element.content.coach && !element.content.optional);

  const isPercentageCorrect = elements
    .filter((element) => element.content?.percentageIdList)
    .every(
      (each) =>
        getPercentageSum(
          totalData || [],
          each.content?.percentageIdList || []
        ) === 100
    );

  const isQuizCorrect = elements
    .filter((element) => element.content?.answerList)
    .every((each) => ifQuizAnswerCorrect(data, each));

  const isAudioPlayerEnded = elements
    .filter(
      (element) =>
        element.type === "audioPlayer" &&
        (element.content as AudioPlayerType)?.audioUrl
    )
    .every((each) => (each.content as AudioPlayerType).isEnded);

  const isAllPercentageInputFilled = elements
    .filter((element) => element.type === "percentage")
    .every((each) => {
      const content = each.content as PercentageType;
      if (content.warnIfZero) {
        return !!content.value && Number(content.value) > 0;
      }
      return !!content.value;
    });

  let isAllTextareaFilled: boolean;
  const textAreaList = elements.filter(
    (element) => element.type === "textarea"
  );

  if (
    textAreaList.some((e) =>
      e?.content?.id?.startsWith("thoughtWithThoughtTrap")
    )
  ) {
    // 생함
    isAllTextareaFilled = textAreaList
      .filter((e) => e?.content?.id?.startsWith("thoughtWithThoughtTrap"))
      .some(
        (each) =>
          ((each.content as TextareaType)?.value || "").trim().length > 0
      );
  } else {
    isAllTextareaFilled = textAreaList.every(
      (each) => ((each.content as TextareaType)?.value || "").trim().length > 0
    );
  }

  const isAllSelectFilled = elements
    .filter((element) => element.type === "select")
    .every((each) => (each.content as SingleSelectionType)?.value);

  const isAllButtonGroupClicked = elements
    .filter((element) => element.type === "buttongroup")
    .every((each) => (each.content as SingleSelectionType)?.value);

  const isAllButtonGroupMultipleChoiceClicked = elements
    .filter((element) => element.type === "buttongroup_multipleChoice")
    .every(
      (each) =>
        ((each.content as MultipleSelectionType)?.selectedIndices || [])
          .length > 0 &&
        (!(each.content as MultipleSelectionType)?.requiredSelectionCount ||
          ((each.content as MultipleSelectionType)?.selectedIndices || [])
            .length ===
            (each.content as MultipleSelectionType)?.requiredSelectionCount)
    );

  const isAllRadioSelected = elements
    .filter((element) => element.type === "selfCheck")
    .every(
      (each) => (each.content as SelfCheckType)?.selectedScore !== undefined
    );

  const pushNotificationButtonCell = elements.find(
    (element) => element.type === "pushNotification"
  );
  const isPushNotificationFilled =
    !pushNotificationButtonCell ||
    (pushNotificationButtonCell.content as PushNotificationType).clicked;

  const feedback = elements
    .find((element) => element.type === "feedback")
    ?.content.value?.trim();
  const isFeedbackSent = !feedback || feedback.length === 0;

  const hasAiTypography = elements.find(
    (element) => element.type === "aiTypography"
  );
  const aiTypography = elements
    .find((element) => element.type === "aiTypography")
    ?.content.value?.trim();
  const aiTypographySet = !hasAiTypography || (aiTypography || "").length > 0;

  const isSelectIntermediateBeliefFilled = elements
    .filter((element) => element.type === "selectIntermediateBelief")
    .every(
      (each) => ((each.content as ContentType)?.value || "").trim().length > 0
    );

  return (
    isPushNotificationFilled &&
    isPercentageCorrect &&
    isQuizCorrect &&
    isAudioPlayerEnded &&
    isAllPercentageInputFilled &&
    isAllTextareaFilled &&
    isAllSelectFilled &&
    isAllButtonGroupClicked &&
    isAllButtonGroupMultipleChoiceClicked &&
    isAllRadioSelected &&
    isFeedbackSent &&
    aiTypographySet &&
    isSelectIntermediateBeliefFilled
  );
}

export function isAllUserFieldFilled(
  data?: ProgramContentType[],
  exceptComment?: boolean,
  user?: UserType
) {
  if (data && data.length > 0) {
    const reversedData = _.cloneDeep(data)?.reverse();
    const reversed = reversedData.findIndex((element) => element.isShown);
    const lastIndex = data.length - 1 - reversed;

    const isTempDone =
      (lastIndex < data.length &&
        data
          .slice(0, lastIndex + 1)
          .some(
            (element) => element.isShown && !element.isHidden && element.isEnd
          )) ||
      false;

    const isLastShown =
      (!data[data.length - 1].isHidden && data[data.length - 1].isShown) ||
      false;

    const lines = data
      .filter((element) => (exceptComment ? !element.isHighlight : true))
      .filter((element) =>
        isTempDone ? element.isShown && !element.isHidden : true
      )
      .slice(0, isTempDone ? lastIndex + 1 : data.length)
      .filter((element) =>
        isLastShown
          ? !element.isAlwaysHidden && element.isShown && !element.isHidden
          : true
      );

    const isAllBlockFieldFilled = lines.every((each) =>
      isBlockUserFieldFilled(each, data, user)
    );
    // console.log(
    //   lines.filter((element) => !isBlockUserFieldFilled(element, data, user))
    // );
    return (isLastShown || isTempDone) && isAllBlockFieldFilled;
  }
  return false;
}

export function isBlockCoachFieldFilled(data: ProgramContentType) {
  const elements = data.lines.flat();
  const coachInputs = elements.filter(
    (element, index) => (element.content as ElementType)?.coach
  );

  const isBlockCoachFieldFilled = coachInputs
    .filter((element) => !element.content?.optional)
    .every(
      (each) => ((each.content as ElementType)?.value || "").trim().length > 0
    );

  return data.hideIfCoach || data.isHidden || isBlockCoachFieldFilled;
}

export function isAllCoachFieldFilled(data: ProgramContentType[]) {
  const isAllCoachTextareaFilled = data.every((each) =>
    isBlockCoachFieldFilled(each)
  );
  return isAllCoachTextareaFilled;
}

export function getReferenceBlock(data: ProgramContentType[], blockId: string) {
  const referenceBlock = data.find(
    (element) => (element as ProgramContentType)?.blockId === blockId
  );
  return referenceBlock;
}

export function getReferenceBlockWithCellId(
  data: ProgramContentType[],
  cellId: string
) {
  const referenceBlock = data.find(
    (element) =>
      (element as ProgramContentType)?.lines
        .flat(2)
        .findIndex((element) => (element as CellType)?.content.id === cellId) >
      -1
  );
  return referenceBlock;
}

export function getReferenceCell(data: ProgramContentType[], id: string) {
  const blockList = data.filter((element) =>
    element.lines.flat().find((element) => element.content.id === id)
  );
  // const cellList: CellType[][][] = data
  //   ?.filter((element) => element.isShown || true) //id가 중복인 경우 보여진 블록만 보기(ex.meditation_time)
  //   ?.map((each) => each.lines) as CellType[][][];
  const cellList: CellType[][][] = (
    blockList.length > 1
      ? blockList.filter((element) => element.isShown && !element.isHidden)
      : blockList
  ) //id가 중복인 경우 보여진 블록만 보기(ex.meditation_time)
    ?.map((each) => each.lines) as CellType[][][];

  const filteredArray = cellList
    .flat(2)
    .filter((element) => (element as CellType)?.content.id === id);
  // const referenceData = cellList
  //   .flat(2)
  //   .find((element) => (element as CellType)?.content.id === id);
  const referenceData = filteredArray
    ? filteredArray[filteredArray.length - 1]
    : undefined;
  return referenceData;
}

export function setReferenceCellAnswer(
  data: ProgramContentType[],
  quizId: string,
  answerIndex: number
) {
  const cellList: CellType[][][] = data?.map(
    (each) => each.lines
  ) as CellType[][][];

  cellList
    .flat(2)
    .find((element) =>
      (element as CellType)?.content.answerList
        ?.map((each) => each.id)
        .includes(quizId)
    )
    ?.content.answerList?.forEach((each) => {
      if (each.id === quizId) {
        each.answerIndex = answerIndex;
      }
    });
}

export function getReferenceData(data: ProgramContentType[], id: string) {
  return getReferenceCell(data, id)?.content;
}

export const getTextMappingFromDataMapping = async ({
  taskKey,
  data,
  dataMapping,
  isCoach,
  accessToken,
  pid,
  t,
  taskDataState,
  setTaskDataState,
}: {
  taskKey: string;
  data: ProgramContentType[];
  dataMapping: DataMappingType;
  isCoach: boolean;
  accessToken: string;
  pid?: number;
  t: TFunction<Namespace, KeyPrefix<FallbackNs<Namespace>>>;
  taskDataState: TempTaskDataType;
  setTaskDataState: React.Dispatch<React.SetStateAction<TempTaskDataType>>;
}) => {
  // taskKey 는 서버가 만든 `taskId:patientId` 라 patientAtom 이 아직 비어 있어도 항상 정확하다.
  // (`:` 가 없으면 NaN → pid 폴백)
  const patientId = getPatientIdFromTaskKey(taskKey) || pid;
  const textMapping: { [key: string]: string } = {};
  for (const key of Object.keys(dataMapping)) {
    let currentData = data;
    if (dataMapping[key].taskId) {
      let taskId = dataMapping[key].taskId;

      if (taskId?.startsWith("4-2-A_")) {
        //영향정도
        const automaticThoughtId = taskId.split("_")[1];
        try {
          const response = await axios(accessToken).get("/dashboard/detail", {
            params: { pid: patientId },
          });

          if (response.data.code === 0) {
            const data = response.data;
            const thoughtTrapList = data.result.concept[
              `automaticThought${automaticThoughtId}`
            ].thoughtTrapList as string[];
            const currentIndex = (
              data.result.concept[`automaticThought${automaticThoughtId}`]
                .thoughtTrapList as string[]
            ).findIndex((element) => element === taskKey);
            const previousThoughtTrapList =
              currentIndex === -1
                ? thoughtTrapList
                : thoughtTrapList.slice(0, currentIndex);

            if (previousThoughtTrapList && previousThoughtTrapList.length > 0) {
              taskId =
                previousThoughtTrapList[
                  previousThoughtTrapList.length - 1
                ].split(":")[0];
            }
          } else {
          }
        } catch (error: any) {}
      }

      try {
        if (taskDataState[`${taskId}:${patientId}`]) {
          //taskDataState에 한 번 저장한 후
          currentData = taskDataState[`${taskId}:${patientId}`];
        } else if (patientId) {
          //처음
          const response = await axios(accessToken).get("/task/data/load", {
            params: { tk: `${taskId}:${patientId}` },
          });

          if (response.data.code === 0) {
            const data = response.data;
            currentData = data.result.data.content;
            setTaskDataState((state) => ({
              ...state,
              [`${taskId}:${patientId}`]: currentData,
            }));
          }
        }
      } catch (error: any) {}
    }
    if (dataMapping[key].value) {
      textMapping[key] = dataMapping[key].value || "";
    } else if (dataMapping[key].id !== undefined) {
      const referenceCell = getReferenceCell(
        currentData,
        dataMapping[key].id as string
      );
      const referenceData = referenceCell?.content;

      if (referenceData) {
        if (
          referenceCell?.type === "select" ||
          referenceCell?.type === "buttongroup" ||
          (referenceCell?.type === "preset" &&
            (referenceCell.content as PresetType).presetKey ===
              "statementSubject")
        ) {
          const options = (referenceData as SingleSelectionType).options || [];
          const selectedIndex = (referenceData as SingleSelectionType)
            .selectedIndex;
          if (selectedIndex !== undefined) {
            textMapping[key] = options[selectedIndex].translationKey
              ? t(
                  (dataMapping[key].additional
                    ? options[selectedIndex].translationKey_additional
                    : options[selectedIndex].translationKey) || ""
                )
              : referenceData?.value ||
                (dataMapping[key].taskId
                  ? `<${getTaskTitleFromTaskId(
                      dataMapping[key].taskId || ""
                    )}>를 먼저 진행해주세요`
                  : "");
          } else {
            textMapping[key] =
              referenceData?.value ||
              (referenceData?.translationKey
                ? t(referenceData?.translationKey || "")
                : "");
          }
        } else if (
          referenceCell?.type === "buttongroup_multipleChoice" &&
          dataMapping[key].index !== undefined
        ) {
          textMapping[key] =
            referenceCell.content.value?.split("&&")[
              dataMapping[key].index || 0
            ] || "";
        } else if (
          referenceCell?.type === "temp" &&
          referenceCell.content.translationKey
        ) {
          textMapping[key] = t(referenceCell.content.translationKey);
        } else if (referenceCell?.type === "typographyFromData") {
          textMapping[key] = referenceCell.content.value || "";
        } else {
          textMapping[key] =
            (isCoach &&
            ["textarea", "preset"].includes(referenceCell?.type || "")
              ? convertHtmlStringToPlainString(
                  (referenceData as TextareaType)?.htmlString || ""
                ).trim()
              : referenceData?.translationKey
                ? t(referenceData?.translationKey)
                : (referenceData?.value || "").trim()) ||
            (dataMapping[key].taskId
              ? `<${getTaskTitleFromTaskId(
                  dataMapping[key].taskId || ""
                )}>를 먼저 진행해주세요`
              : "");
        }
      }
    }
  }

  return textMapping;
};

export function isLineHidden(
  data: ProgramContentType[],
  blockData: ProgramContentType,
  line: CellType[]
) {
  return line.every(
    (each) =>
      each.type === "temp" ||
      each.content.isHidden ||
      (each.content?.requiredId &&
        getReferenceData(data, each.content?.requiredId)?.optional &&
        (getReferenceData(data, each.content?.requiredId)?.value || "").trim()
          .length === 0) ||
      (each.content?.percentageIdList &&
        getPercentageSum(data, each.content?.percentageIdList) === 100) ||
      (each.content?.answerList && !ifIncorrectTextShow(blockData, each))
  );
}

// export function ifIncorrectTextShow(data: ProgramContentType, cell: CellType) {
//   return (
//     cell.content?.answerList?.every(
//       (each) => getReferenceData([data], each.id)?.value
//     ) && !ifQuizAnswerCorrect(data, cell)
//   );
// }

// export function ifQuizAnswerCorrect(data: ProgramContentType, cell: CellType) {
//   return cell.content?.answerList?.every(
//     (each) => getReferenceData([data], each.id)?.value === each.value
//   );
// }
export function ifIncorrectTextShow(data: ProgramContentType, cell: CellType) {
  return (
    cell.content?.answerList?.every(
      (each) =>
        (getReferenceData([data], each.id) as SingleSelectionType)
          ?.selectedIndex !== undefined
    ) && !ifQuizAnswerCorrect(data, cell)
  );
}

export function ifQuizAnswerCorrect(data: ProgramContentType, cell: CellType) {
  return cell.content?.answerList?.every((each) =>
    each.value
      ? (getReferenceData([data], each.id) as SingleSelectionType)?.value ===
        each.value
      : (getReferenceData([data], each.id) as SingleSelectionType)
          ?.selectedIndex === each.answerIndex
  );
}

export function getPercentageSum(
  data: ProgramContentType[],
  percentageIdList: string[]
) {
  return arraySum(
    percentageIdList?.map(
      (each) => parseInt(getReferenceData(data, each)?.value || "") || 0
    ) || []
  );
}

function getSelfCheckScoreById(selfCheckId: string, data?: SelfCheckType[]) {
  return arraySum(
    data
      ?.filter((element) => element.selfCheckId === selfCheckId)
      .map((each) => each.selectedScore || 0) || [0]
  );
}

export function getSelfCheckScore(data?: ProgramContentType[]) {
  const elements = data?.map((each) => each.lines.flat()).flat();
  const radioInputs = elements
    ?.filter((element) => element.type === "selfCheck")
    .map((each) => each.content as SelfCheckType);

  return {
    depressionScore: getSelfCheckScoreById("depression", radioInputs),
    anxietyScore: getSelfCheckScoreById("anxiety", radioInputs),
    stressScore: getSelfCheckScoreById("stress", radioInputs),
    burnoutScore: getSelfCheckScoreById("burnout", radioInputs),
  };
}

export function getTaskIdFromTaskKey(taskKey: string) {
  return taskKey.split(":")[0];
}

export function getPatientIdFromTaskKey(taskKey: string) {
  return Number(taskKey.split(":")[1]);
}

export function getRawTaskTitleFromTaskId(
  taskIdWithAutomaticThoughtId: string
) {
  const taskId = taskIdWithAutomaticThoughtId.split("_")[0].split("#")[0];
  const step = taskIdWithAutomaticThoughtId.split("_")[0].split("#")[1];
  const title =
    getTaskEditorTaskTitle(taskId) ||
    TaskMetaData.find((element) => element.taskId === taskId)?.title ||
    "";
  return title.includes("n") && false ? title.replace("n", step) : title;
}

export function getTaskTitleFromTaskId(taskKey: string) {
  const taskId = getTaskIdFromTaskKey(taskKey);

  const N = taskId.split("#")[1] as automaticThoughtIdType;

  const title =
    taskKey === "dashboard"
      ? "대시보드"
      : taskKey === "thoughtmap"
        ? "생각 그물"
        : taskKey === "valueCompass"
          ? "가치 나침반"
          : taskKey === "progress"
            ? "진행 상황"
            : taskKey === "payment"
              ? "월 이용권"
              : taskKey === "chat"
                ? "채팅"
                : getRawTaskTitleFromTaskId(taskId);
  const postfix = taskId.includes("_")
    ? ` (${taskId.split("_")[1].split("#")[0]})`
    : "";

  return `${(title || "").replace("n", N)}${postfix}`;
}

export function getRedirectPath(user?: UserType) {
  if (!user || !user?.accessToken) {
    // if (!user?.appId) {
    //   return "/onboarding/guest/1";
    // }
    return "/login";
  } else {
    return "/";
  }
}

export function getLastShownIndex(data?: ProgramContentType[]) {
  if (data) {
    const copy = _.cloneDeep(data);
    const reversedData = copy?.reverse();
    const reversed = reversedData.findIndex(
      (element) => element.isShown && !element.isHidden
    );
    const lastIndex = (data.length - 1 - reversed) % data.length;
    return lastIndex;
  } else {
    return 0;
  }
}

export function generateThoughtRecordKey(userId: number) {
  return `${userId}:${Date.now()}`;
}

export function generateRecordKey(
  userId: number,
  type: "meditation" | "activity"
) {
  return `${
    type === "meditation" ? "mr" : type === "activity" ? "ar" : ""
  }:${userId}:${Date.now()}`;
}

export function getGroupName(taskId: string) {
  // taskId 변종 (예: "3-3-A_1", "7-7-A_1#1") 의 접미사를 떼고 베이스 taskId 로 매칭한다.
  const baseTaskId = taskId.split("_")[0].split("#")[0];
  return TaskMetaData.find((element) => element.taskId === baseTaskId)?.group;
}

export function getTaskIdPrefix(taskId: string) {
  return taskId.split("-").splice(0, 2).join("-");
}

export function getLanguageCode(locale: string) {
  return locale.split("-")[0];
}

export function findSubstringIndices(mainString: string, subString: string) {
  const startIndex = mainString.indexOf(subString);

  if (startIndex !== -1) {
    const endIndex = startIndex + subString.length - 1;
    return { startIndex, endIndex };
  } else {
    // 부분 문자열이 포함되어 있지 않은 경우
    return { startIndex: -1, endIndex: -1 };
  }
}

function convertHtmlStringToPlainString(html: string) {
  // Create a new div element
  var tempDivElement = document.createElement("div");

  // Set the HTML content with the given value
  tempDivElement.innerHTML = html;

  // Retrieve the text property of the element
  return tempDivElement.textContent || tempDivElement.innerText || "";
}

export function getHighlightedBlockNumber(data: ProgramContentType[]) {
  return data.filter((element) => element.isHighlight).length;
}

export function getAutomaticThoughtId(taskId: string) {
  const automaticThoughtId = taskId.includes("_")
    ? taskId.split("_")[1]
    : undefined;
  return automaticThoughtId;
}

export function isNegativeAutomaticThought(id: string) {
  return (
    id.startsWith("automaticThought") &&
    !id.startsWith("automaticThoughtForValue")
  );
}

export function isPositiveAutomaticThought(id: string) {
  return id.startsWith("automaticThoughtForValue");
}

export function getAutomaticThoughtIdList(data: ConceptType | ConceptNoteType) {
  return Object.keys(data)
    .filter((element) => isNegativeAutomaticThought(element))
    .map((each) => each.split("automaticThought")[1]);
}

export function getAutomaticThoughtForValueIdList(
  data: ConceptType | ConceptNoteType
) {
  return Object.keys(data)
    .filter((element) => isPositiveAutomaticThought(element))
    .map((each) => each.split("automaticThoughtForValue")[1]);
}

export function automaticThoughtIdListWithBlank(
  data: ConceptType | ConceptNoteType
) {
  const filledAutomaticThoughtIdList = getAutomaticThoughtIdList(data);
  const lastFilledAutomaticThoughtId =
    filledAutomaticThoughtIdList.length > 0
      ? +filledAutomaticThoughtIdList[filledAutomaticThoughtIdList.length - 1]
      : 0;
  return filledAutomaticThoughtIdList.concat([
    (lastFilledAutomaticThoughtId + 1).toString(),
  ]);
}

export function getExtraAutomaticThoughtKey(
  data: ConceptType | ConceptNoteType
) {
  const filledAutomaticThoughtIdList = getAutomaticThoughtIdList(data);
  const lastFilledAutomaticThoughtId =
    filledAutomaticThoughtIdList.length > 0
      ? +filledAutomaticThoughtIdList[filledAutomaticThoughtIdList.length - 1]
      : 0;

  return `automaticThought${lastFilledAutomaticThoughtId + 1}`;
}

export function hasAiComment(data: ProgramContentType) {
  const elements = data.lines.flat();
  const coachInputs = elements.filter(
    (element) => (element.content as ElementType)?.coach
  );

  const hasAiComment = coachInputs.some(
    (each) => (each.content as AiCommentType)?.aiCommentKey
  );

  return hasAiComment;
}

export function getBlockIndexWithEditorKey(
  data: ProgramContentType[],
  editorKey: string
) {
  const index = data.findIndex((element) =>
    (element as ProgramContentType)?.lines
      .flat()
      .find((each) => each.content.editorKey === editorKey)
  );
  return index;
}

function isAiComment(
  cell: CellType
): cell is CellType & { content: AiCommentType } {
  return cell.type === "aiComment" && "aiCommentKey" in cell.content;
}

export function hasAiCommentKey(
  blocks: ProgramContentType[] | undefined,
  aiCommentKey: string
): boolean {
  if (!Array.isArray(blocks)) return false;

  // ① lines 가 undefined 인 블록은 제거
  const everyLineArrays = blocks
    .map((b) => b?.lines)
    .filter((l): l is CellType[][] => Array.isArray(l));

  // ② 모든 depth 를 평탄화
  //    - Node 16+ : .flat(Infinity) 가능
  //    - 그 이하 : 재귀 flatten 함수 사용
  const cells: CellType[] = (everyLineArrays as unknown as any[]).flat(
    Infinity
  ) as CellType[];

  // ③ type 이 aiComment 인 셀만 필터링 후 key 비교
  return cells
    .filter(
      (e) =>
        isAiComment(e) &&
        e?.type === "aiComment" &&
        (e.content as AiCommentType).aiCommentKey
    )
    .flatMap((e) => (e.content as AiCommentType).aiCommentKey)
    .some((e) => e === aiCommentKey);
}

export function getAiCommentKeyInBlock(blockData: ProgramContentType) {
  const aiCommentKey = (
    blockData.lines.flat().find((element) => element.type === "aiComment")
      ?.content as AiCommentType
  )?.aiCommentKey;
  return aiCommentKey;
}

export function addAiComment(
  isThoughtRecord: boolean,
  setData: React.Dispatch<
    React.SetStateAction<ProgramContentType[] | undefined>
  >,
  aiCommentList?: CommentType[]
): number | undefined {
  let addedIndex: number | undefined = undefined;

  if ((aiCommentList || []).length > 0) {
    setData((data) => {
      if (data) {
        const data_temp = _.cloneDeep(data);

        aiCommentList!!.forEach((each) => {
          const blockIndex = getBlockIndexWithEditorKey(
            data_temp,
            each.editorKey
          );
          if (blockIndex > -1) {
            const newComments = each.commentList
              .filter(
                ({ aiCommentKey }) => !hasAiCommentKey(data_temp, aiCommentKey)
              )
              .map(({ aiCommentKey, comment }) => {
                let questionId: string | undefined;
                let answerId: string | undefined;

                if (isThoughtRecord) {
                  const currentData = data_temp
                    .slice(0, blockIndex + 1)
                    .reverse()
                    .find((element) => !element.indentation);

                  const currentDataIndex = data_temp.findIndex((element) =>
                    _.isEqual(element, currentData)
                  );

                  const id = currentData?.lines
                    .flat()
                    .find((element) => element?.content?.id)?.content?.id;

                  const uuid = uuidv4().split("-")[0];

                  questionId = `${id}_question_${uuid}`;
                  answerId = `${id}_answer_${uuid}`;
                }

                return AiCommentWithTextareaInput(
                  aiCommentKey,
                  comment,
                  questionId,
                  answerId,
                  {
                    isHighlight: true,
                  }
                );
              });

            if (newComments.length > 0) {
              data_temp.splice(blockIndex + 1, 0, ...newComments);
              if (addedIndex === undefined) {
                addedIndex = blockIndex + 1; // 첫 번째 추가된 AiComment의 index를 저장
              }
            }
          }
        });

        return data_temp;
      }
      return data;
    });
  }

  return addedIndex;
}

export function setProgramContentData({
  setData,
  blockIndex,
  lineIndex,
  cellIndex,
  newlyAddedData,
}: {
  setData: React.Dispatch<
    React.SetStateAction<ProgramContentType[] | undefined>
  >;
  blockIndex: number;
  lineIndex: number;
  cellIndex: number;
  newlyAddedData: any;
}) {
  setData((data) => {
    if (
      data &&
      data[blockIndex] &&
      data[blockIndex].lines &&
      data[blockIndex].lines[lineIndex] &&
      data[blockIndex].lines[lineIndex][cellIndex]
    ) {
      const data_temp = _.cloneDeep(data);
      const currentContent =
        data_temp[blockIndex].lines[lineIndex][cellIndex].content;

      data_temp[blockIndex].lines[lineIndex][cellIndex].content = {
        ...currentContent,
        ...newlyAddedData,
      };

      return data_temp;
    }
    return data;
  });
}

export function createAudioInstanceKey(
  taskKey: string | undefined,
  meditationRecordKey: string | undefined,
  url: string | undefined
): string {
  const tk = taskKey || "noTaskKey";
  const mrk = meditationRecordKey || "noMrk";
  const safeUrl = url || "noUrl";
  const urlIdentifier =
    safeUrl.substring(safeUrl.lastIndexOf("/") + 1) || safeUrl;

  return `audioInstance-${tk}-${mrk}-${urlIdentifier}`;
}

export function clearOldCoachCommentHighlights(
  data?: ProgramContentType[]
): ProgramContentType[] | undefined {
  if (!data) return data;

  return data.map((block) => {
    try {
      if (!block?.indentation) return block;

      // Flatten block lines to inspect cells
      const flat = (block.lines || []).flat();

      // Detect if this block contains a coach textarea (coach comment block)
      const hasCoachTextarea = flat.some(
        (cell: any) =>
          cell?.type === "textarea" && (cell?.content?.coach ?? false)
      );
      if (!hasCoachTextarea) return block; // Not a coach comment block

      // Find patient's answer cell (non-coach textarea) and read its value
      const answerCell = flat.find(
        (cell: any) =>
          cell?.type === "textarea" && !(cell?.content?.coach ?? false)
      );
      const answerValue = (answerCell?.content?.value || "").trim();

      // If there's an answer already, remove highlight for old coach comments
      if (answerValue.length > 0 && block.isHighlight) {
        return { ...block, isHighlight: false } as ProgramContentType;
      }
    } catch (e) {
      // In case of any unexpected structure, keep the original block
    }
    return block;
  });
}

/**
 * 코멘트 블록에 내담자(비-coach)가 작성한 답변이 있는지 판별.
 * line 0 = 코치/AI 코멘트(coach:true), line 1 = 내담자 답변 textarea(coach !== true).
 * clearOldCoachCommentHighlights 의 답변 감지 패턴과 동일.
 */
export function blockHasPatientAnswer(block?: ProgramContentType): boolean {
  if (!block) return false;
  const flat = (block.lines || []).flat();
  const answerCell = flat.find(
    (cell: any) => cell?.type === "textarea" && !(cell?.content?.coach ?? false)
  );
  return ((answerCell?.content?.value as string) || "").trim().length > 0;
}

/**
 * 과거 AiCommentWithTextareaInput 이 noArrow: true 로 저장한 AI 코멘트 블록을 정규화.
 * noArrow 가 남아 있으면 이 블록이 마지막 shown 블록이 되는 순간 다음 블록을 열
 * 수단(화살표·자동 열기)이 없어 활동지가 영구히 멈춘다. 로드 시점에 제거한다.
 */
export function clearAiCommentNoArrow(
  data?: ProgramContentType[]
): ProgramContentType[] | undefined {
  if (!data) return data;

  return data.map((block) => {
    if (!block?.noArrow) return block;
    const flat = (Array.isArray(block.lines) ? block.lines : []).flat();
    if (!flat.some((cell: any) => cell?.type === "aiComment")) return block;
    const { noArrow, ...rest } = block;
    return rest as ProgramContentType;
  });
}
